import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IDocumentData } from '@models/document/IDocumentData';
import { IContentModels, IDocumentScheme } from '@models/document/IDocumentScheme';
import { IDocumentStatusBar } from '@models/document/IDocumentStatusBar';
import { IDocumentActions, IDocumentTitleBarActions } from '@models/actions/IDocumentActions';
import { IBaseAction } from '@models/actions/IBaseAction';
import { IAllRoutesActions } from '@models/actions/IAllRoutesActions';
import { IDocumentRouteMap, IDocumentRouteMapAction } from '@models/document/IDocumentRouteMap';
import { IWarningAction } from '@models/document/IDocumentResult';
import {
    ICopyAndEditAttachesAddResponse,
    ICopyAndEditAttachesData,
    ICopyAndEditAttachesScheme,
} from '@models/attaches/ICopyAndEditAttaches';
import { SLICE_NAMES } from '@/constants';

export interface IDocumentModalState {
    data: IDocumentData;
    scheme: IDocumentScheme;
}

export interface IDocumentState {
    isEdit: boolean;
    isNew: boolean;
    isHistoryView: boolean;
    isLoading: boolean;
    isLoadingStatusBar: boolean;
    isLoadingTitleBarActions: boolean;
    isLoadingMenuDocumentActions: boolean;
    isLoadingMenuAllRoutesActions: boolean;
    loadError?: string;
    isFavorite: boolean;
    idDocHashCheck: boolean;
    scheme?: IDocumentScheme;
    /**
     * Контент-модели (XML-схемы формы и линкованных потоков). Грузятся лениво
     * для технических инспекторов: Debug Panel и production-коррекции реквизитов.
     */
    contentModels?: IContentModels;
    isLoadingContentModels?: boolean;
    /** Документ последней загрузки контент-моделей: ответ по другому документу не применяется. */
    contentModelsDocId?: string;
    data?: IDocumentData;
    statusBar?: IDocumentStatusBar;
    actions: IDocumentActions;
    routeMap: IDocumentRouteMap;
    warningAction?: IWarningAction;
    partsVersionToAttach?: string[];
    partsVersionToCopy?: ICopyAndEditAttachesAddResponse[];
    newDocCopyAttachesScheme?: ICopyAndEditAttachesScheme;
    newDocCopyAttachesData?: ICopyAndEditAttachesData;
}

const initialState: IDocumentState = {
    isEdit: false,
    isNew: false,
    isHistoryView: false,
    isLoading: true,
    isLoadingStatusBar: true,
    isLoadingTitleBarActions: true,
    isLoadingMenuDocumentActions: false,
    isLoadingMenuAllRoutesActions: false,
    loadError: undefined,
    isFavorite: false,
    idDocHashCheck: false,
    scheme: undefined,
    contentModels: undefined,
    isLoadingContentModels: false,
    data: undefined,
    statusBar: undefined,
    actions: {
        titleBarActions: undefined,
        menuActions: {
            documentActions: undefined,
            allRoutesActions: undefined,
            allRoutesActionsEpoch: 0,
            allRoutesActionsLoadedEpoch: 0,
        },
    },
    routeMap: {
        addAction: { isVisibleAction: false, actionConfig: undefined },
    },
    partsVersionToAttach: undefined,
    partsVersionToCopy: undefined,
    newDocCopyAttachesScheme: undefined,
    newDocCopyAttachesData: undefined,
};

export const DocumentSlice = createSlice({
    name: SLICE_NAMES.DOCUMENT,
    initialState,
    reducers: {
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsLoadingStatusBar: (state, action: PayloadAction<boolean>) => {
            state.isLoadingStatusBar = action.payload;
        },
        setIsLoadingTitleBarActions: (state, action: PayloadAction<boolean>) => {
            state.isLoadingTitleBarActions = action.payload;
        },
        setIsLoadingMenuDocumentActions: (state, action: PayloadAction<boolean>) => {
            state.isLoadingMenuDocumentActions = action.payload;
        },
        setIsLoadingMenuAllRoutesActions: (state, action: PayloadAction<boolean>) => {
            state.isLoadingMenuAllRoutesActions = action.payload;
        },
        setLoadError: (state, action: PayloadAction<string | undefined>) => {
            state.loadError = action.payload;
        },
        setIsEdit: (state, action: PayloadAction<boolean>) => {
            state.isEdit = action.payload;
        },
        setIsNew: (state, action: PayloadAction<boolean>) => {
            state.isNew = action.payload;
        },
        setIsHistoryView: (state, action: PayloadAction<boolean>) => {
            state.isHistoryView = action.payload;
        },
        setIsFavorite: (state, action: PayloadAction<boolean>) => {
            state.isFavorite = action.payload;
        },
        setIdDocHashCheck: (state, action: PayloadAction<boolean>) => {
            state.idDocHashCheck = action.payload;
        },
        setRouteMapAddActionConfig: (state, action: PayloadAction<IDocumentRouteMapAction>) => {
            state.routeMap.addAction = action.payload;
        },
        successLoadScheme: (state, action: PayloadAction<IDocumentScheme>) => {
            state.scheme = action.payload;
        },
        startLoadContentModels: (state, action: PayloadAction<string>) => {
            state.contentModelsDocId = action.payload;
            state.isLoadingContentModels = true;
        },
        successLoadContentModels: (state, action: PayloadAction<{ docId: string; contentModels: IContentModels }>) => {
            if (action.payload.docId !== state.contentModelsDocId) return;
            state.contentModels = action.payload.contentModels;
        },
        finishLoadContentModels: (state, action: PayloadAction<string>) => {
            if (action.payload !== state.contentModelsDocId) return;
            state.isLoadingContentModels = false;
        },
        successLoadData: (state, action: PayloadAction<IDocumentData>) => {
            state.data = action.payload;
        },
        reset: (state, action: PayloadAction<{ keepTitleBarActions?: boolean; keepContent?: boolean } | undefined>) => {
            // Повторная загрузка того же документа работает как fetch-then-swap: старая карточка
            // остаётся под блокирующим overlay, пока новые data + scheme не готовы. Холодное
            // открытие по-прежнему полностью очищает состояние.
            if (!action.payload?.keepContent) {
                state.isEdit = false;
                state.isNew = false;
                state.scheme = undefined;
                state.contentModels = undefined;
                state.isLoadingContentModels = false;
                state.contentModelsDocId = undefined;
                state.data = undefined;
                state.isHistoryView = false;
                state.statusBar = undefined;
                state.partsVersionToAttach = undefined;
                state.partsVersionToCopy = undefined;
                state.newDocCopyAttachesScheme = undefined;
                state.newDocCopyAttachesData = undefined;
            }
            state.loadError = undefined;
            // keepTitleBarActions: при doc_Hash-reload с УСТАНОВЛЕННЫМ realtime сохраняем кнопки
            // заголовка (их освежает coalesced refresh — fetch-then-swap), иначе обнуление даёт
            // «моргание» кнопок (исчезли → preloader → новые).
            if (!action.payload?.keepTitleBarActions) {
                state.actions.titleBarActions = undefined;
            }
            state.actions.menuActions.documentActions = undefined;
            // Повторное открытие того же документа держит маршрутный набор до подмены
            // свежим — иначе бейдж на кнопке «Действия» моргает.
            if (action.payload?.keepContent) {
                state.actions.menuActions.allRoutesActionsEpoch++;
            } else {
                state.actions.menuActions.allRoutesActions = undefined;
            }
        },
        resetActions: (state) => {
            state.actions.titleBarActions = undefined;
            state.actions.menuActions.documentActions = undefined;
            state.actions.menuActions.allRoutesActions = undefined;
        },
        /**
         * Узкий сброс ТОЛЬКО ленивых меню (без titleBarActions): фоновая realtime-
         * инвалидация действий по ревизии перечитывает титлбар на месте и не должна
         * «моргать» кнопками заголовка, обнуляя titleBarActions. Документные операции
         * сбрасываются, чтобы следующее открытие перечитало актуальный набор; маршрутный
         * набор (бейдж кнопки) только помечается устаревшим и подменяется свежим.
         */
        resetMenuActions: (state) => {
            state.actions.menuActions.documentActions = undefined;
            state.actions.menuActions.allRoutesActionsEpoch++;
        },
        successLoadStatusBar: (state, action: PayloadAction<IDocumentStatusBar>) => {
            state.statusBar = action.payload;
        },
        successLoadTitleBarActions: (state, action: PayloadAction<IDocumentTitleBarActions>) => {
            state.actions.titleBarActions = action.payload;
        },
        successLoadMenuDocumentActions: (state, action: PayloadAction<IBaseAction[]>) => {
            state.actions.menuActions.documentActions = action.payload;
        },
        successLoadMenuAllRoutesActions: (state, action: PayloadAction<IAllRoutesActions>) => {
            state.actions.menuActions.allRoutesActions = action.payload;
            state.actions.menuActions.allRoutesActionsLoadedEpoch = state.actions.menuActions.allRoutesActionsEpoch;
        },
        setActionWarning: (state, action: PayloadAction<IWarningAction | undefined>) => {
            state.warningAction = action.payload;
        },
        setPartsVersionToAttach: (state, action: PayloadAction<string[]>) => {
            state.partsVersionToAttach = action.payload;
        },
        setPartsVersionToCopy: (state, action: PayloadAction<ICopyAndEditAttachesAddResponse[]>) => {
            state.partsVersionToCopy = action.payload;
        },
        successLoadNewDocCopyAttachesScheme: (state, action: PayloadAction<ICopyAndEditAttachesScheme>) => {
            state.newDocCopyAttachesScheme = action.payload;
        },
        successLoadNewDocCopyAttachesData: (state, action: PayloadAction<ICopyAndEditAttachesData>) => {
            state.newDocCopyAttachesData = action.payload;
        },
    },
});

export const { setIsLoading, setIsEdit, successLoadScheme, successLoadData } = DocumentSlice.actions;

export default DocumentSlice.reducer;
